import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Alert, Input } from 'antd';

const TheoDoiDonHangComponent = () => {
  // State lưu trữ dữ liệu từ hai nguồn
  const [vehicles, setVehicles] = useState([]);       // Dữ liệu xe từ /api/get_map_data
  const [ordersData, setOrdersData] = useState([]);     // Dữ liệu đơn hàng từ /api/customer/orders
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  
  // State lọc: trạng thái và tìm kiếm theo mã đơn hàng
  // statusFilter: 'all', 'da_nhan', 'capnhat_chuyen'
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // View của bản đồ (ví dụ: trung tâm TP HCM)
  const [viewState, setViewState] = useState({
    longitude: 106.65126,
    latitude: 10.80266,
    zoom: 14,
  });
  
  const mapRef = useRef();
  
  // Hàm fetch dữ liệu xe từ route /api/get_map_data
  const fetchVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    try {
      const response = await fetch('/api/get_map_data');
      if (!response.ok) {
        throw new Error('Error fetching map data');
      }
      const json = await response.json();
      console.log('Fetched vehicles:', json.data);
      setVehicles(json.data);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoadingVehicles(false);
    }
  }, []);
  
  // Hàm fetch dữ liệu đơn hàng từ route /api/customer/orders
  const fetchOrdersData = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const response = await fetch('/api/customer/orders');
      if (!response.ok) {
        throw new Error('Error fetching orders data');
      }
      const json = await response.json();
      console.log('Fetched orders:', json);
      // Giả sử endpoint trả về mảng hoặc object có thuộc tính data
      setOrdersData(Array.isArray(json) ? json : json.data);
    } catch (error) {
      console.error('Error fetching orders data:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, []);
  
  // Fetch dữ liệu khi component mount và cập nhật định kỳ (mỗi 20 giây)
  useEffect(() => {
    fetchVehicles();
    const intervalVehicles = setInterval(fetchVehicles, 20000);
    return () => clearInterval(intervalVehicles);
  }, [fetchVehicles]);
  
  useEffect(() => {
    fetchOrdersData();
    const intervalOrders = setInterval(fetchOrdersData, 20000);
    return () => clearInterval(intervalOrders);
  }, [fetchOrdersData]);
  
  // Hàm helper: chuẩn hóa chuỗi (loại bỏ dấu '-' và chuyển về chữ in hoa)
  const normalizeString = (value) => {
    return value ? value.replace(/-/g, "").toUpperCase() : "";
  };
  
  // Tính toán danh sách xe cần hiển thị dựa trên bộ lọc trạng thái và tìm kiếm theo mã đơn hàng
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      const normalizedPlate = normalizeString(vehicle.VehiclePlate);
      // Tìm đơn hàng có bien_kiem_soat khớp với VehiclePlate
      const matchingOrder = ordersData.find((order) => {
        const normalizedOrder = normalizeString(order.bien_kiem_soat);
        return normalizedOrder === normalizedPlate;
      });
      if (matchingOrder) {
        // Lọc theo trạng thái
        let statusMatch = false;
        if (statusFilter === 'all') {
          statusMatch = matchingOrder.trang_thai === 'da_nhan' || matchingOrder.trang_thai === 'capnhat_chuyen';
        } else {
          statusMatch = matchingOrder.trang_thai === statusFilter;
        }
        // Lọc theo tìm kiếm: so sánh searchQuery với mã đơn hàng (order.id)
        let searchMatch = true;
        if (searchQuery) {
          searchMatch = matchingOrder.id.toString().includes(searchQuery.trim());
        }
        return statusMatch && searchMatch;
      }
      return false;
    });
  }, [vehicles, ordersData, statusFilter, searchQuery]);
  
  // Hàm render Marker cho một xe (đơn hàng)
  const renderMarker = (vehicle, index) => {
    const latitude = parseFloat(vehicle.Latitude);
    const longitude = parseFloat(vehicle.Longitude);
    if (isNaN(latitude) || isNaN(longitude)) {
      console.warn(`Invalid coordinates for vehicle: ${vehicle.VehiclePlate}`);
      return null;
    }
    return (
      <Marker key={index} latitude={latitude} longitude={longitude}>
        <img
          src={`${process.env.PUBLIC_URL}/images/truck-icon.png`}
          alt="Vehicle"
          style={{ width: '32px', height: '32px', cursor: 'pointer' }}
          onClick={() => setSelectedVehicle(vehicle)}
        />
      </Marker>
    );
  };
  
  // Hàm render nội dung Popup khi một Marker được chọn
  const renderPopupContent = (vehicle) => {
    const normalizedPlate = normalizeString(vehicle.VehiclePlate);
    // Tìm đơn hàng khớp với normalizedPlate
    const matchingOrder = ordersData.find((order) => normalizeString(order.bien_kiem_soat) === normalizedPlate);
    return (
      <div>
        <b>Biển số xe:</b> {vehicle.VehiclePlate} <br />
        {matchingOrder ? (
          <>
            <b>Mã đơn hàng:</b> {matchingOrder.id} <br />
            <b>Tên khách hàng:</b> {matchingOrder.ten_khach_hang} <br />
            <b>Điểm đi:</b> {matchingOrder.diem_di} <br />
            <b>Điểm đến:</b> {matchingOrder.diem_den} <br />
            <b>Loại xe:</b> {matchingOrder.loai_xe} <br />
            <b>Trạng thái đơn hàng:</b> {matchingOrder.trang_thai} <br />
            <b>Mã đơn hàng (tìm kiếm):</b> {matchingOrder.id} <br />
          </>
        ) : (
          <span>Không có đơn hàng phù hợp</span>
        )}
      </div>
    );
  };
  
  return (
    <div style={{ position: 'relative' }}>
      {/* Bộ lọc trạng thái và tìm kiếm */}
      <div style={{ position: 'absolute', top: 20, left: 20, zIndex: 4, background: '#fff', padding: '8px 12px', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
        <span style={{ marginRight: '8px', fontWeight: 'bold' }}>Hiển thị xe:</span>
        <button
          onClick={() => setStatusFilter('all')}
          style={{
            marginRight: '4px',
            padding: '4px 8px',
            background: statusFilter === 'all' ? '#1890ff' : '#f0f0f0',
            color: statusFilter === 'all' ? '#fff' : '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Tất cả
        </button>
        <button
          onClick={() => setStatusFilter('da_nhan')}
          style={{
            marginRight: '4px',
            padding: '4px 8px',
            background: statusFilter === 'da_nhan' ? '#1890ff' : '#f0f0f0',
            color: statusFilter === 'da_nhan' ? '#fff' : '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Đã nhận
        </button>
        <button
          onClick={() => setStatusFilter('capnhat_chuyen')}
          style={{
            padding: '4px 8px',
            background: statusFilter === 'capnhat_chuyen' ? '#1890ff' : '#f0f0f0',
            color: statusFilter === 'capnhat_chuyen' ? '#fff' : '#000',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Cập nhật chuyến
        </button>
        <div style={{ marginTop: '8px' }}>
          <Input
            placeholder="Tìm theo mã đơn hàng"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '200px' }}
          />
        </div>
      </div>
      
      {/* Hiển thị Alert nếu dữ liệu đang tải */}
      {(loadingVehicles || loadingOrders) && (
        <Alert
          message="Đang tải dữ liệu..."
          type="info"
          showIcon
          style={{
            position: 'absolute',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
          }}
        />
      )}
      
      <div style={{ height: '80vh', width: '100%' }}>
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          style={{ width: '100%' }}
          mapStyle={`https://maps.vietmap.vn/api/maps/raster/styles.json?apikey=${process.env.REACT_APP_VIETMAP_API_KEY}`}
          mapboxAccessToken={process.env.REACT_APP_VIETMAP_API_KEY}
        >
          <NavigationControl position="top-left" />
          {filteredVehicles.map((vehicle, index) => renderMarker(vehicle, index))}
          {selectedVehicle && (
            <Popup
              latitude={parseFloat(selectedVehicle.Latitude)}
              longitude={parseFloat(selectedVehicle.Longitude)}
              onClose={() => setSelectedVehicle(null)}
              closeOnClick={false}
            >
              {renderPopupContent(selectedVehicle)}
            </Popup>
          )}
        </Map>
      </div>
    </div>
  );
};

export default TheoDoiDonHangComponent;
